<template>
    <div>
        <v-card color="zoezi-edit-color" dark height="100" v-if="$root.edit && truncatedCourses.length == 0">
            <v-card-title>{{$translate('Course list: No courses to display')}}</v-card-title>
        </v-card>

        <div v-if="showCategoryTitle && categoryName" class="title primary--text text-center my-4" v-text="categoryName" />

        <div v-if="showCategoryTitle && showCategoryDescription && categoryDescription" class="text-center my-4" v-html="categoryDescription" />

        <div v-if="resources.length > 1 && showResourcesFilter" class="ml-2">
            <v-chip
                v-for="resource in resources" :key="resource.id"
                :outlined="!resourcesFilter.find(x => x == resource.id)"
                :close="!!resourcesFilter.find(x => x == resource.id)"
                color="primary"
                :ripple="false"
                label
                class="mr-2 mb-2"
                @click="setProp('resourcesFilter', resourcesFilter.find(x => x == resource.id) ? [] : [resource.id])"
                @click:close="setProp('resourcesFilter', resourcesFilter.find(x => x == resource.id) ? [] : [resource.id])"
            >
                {{resource.name}}
            </v-chip>
        </div>

        <div v-if="showTagsFilter" class="ml-2">
            <v-chip
                v-for="tag in availableTags"
                :key="tag.id"
                :outlined="!tagsFilter.find(x => x == tag.id)"
                :close="!!tagsFilter.find(x => x == tag.id)"
                color="primary"
                :ripple="false"
                label
                class="mr-2 mb-2"
                @click="toggleTagFilter(tag.id)"
                @click:close="toggleTagFilter(tag.id)"
            >
                {{ tag.name }}
            </v-chip>
        </div>

        <card-layout :items="orderedCourses" v-slot="{item: course, layout}">
            <course-card
                :course="course"
                @click="courseDialog.course = course; courseDialog.show = true"
                :title-right="layout == 'wide'"
                :show-price="showPrice"
            />
        </card-layout>

        <v-dialog
            v-if="courseDialog.show"
            v-model="courseDialog.show"
            max-width="850"
        >
            <v-card>
                <course-booking
                    :forceLoading="courseDialog.loading"
                    :courseId="courseDialog.course && courseDialog.course.id"
                    dialog
                    @close="courseDialog.show = false"
                    @course-loaded="(v) => {courseDialog.course = v}"
                    :showSpaceLeft="showSpaceLeft"
                />
                <v-card-actions>
                    <v-spacer></v-spacer>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import CourseCard from './CourseCard.vue'
import CourseBooking from './CourseBooking.vue'
import CardLayout from './CardLayout.vue'
import { intersection } from 'lodash';
import { makeValidPathFromTitle } from '../util.js'
import { debounce } from 'lodash';

export default {
    name: 'zoezi-course-list',

    zoezi: {
        title: 'Course list',
        icon: 'mdi-format-list-text',
        addon: 'Courses',
        getDefaultStyle: () => ({
            pageParams: [
                {
                    id: 'kurs',
                    prop: 'currentCourseName'
                }
            ]
        })
    },

    props: {
        categoryId: {
            title: 'Category',
            type: Number,
            fulltype: 'select',
            typeinfo: {
                multiple: false,
                text: 'name',
                value: 'id',
                getItems: () => window.$booking.getCourseCategories()
            }
        },
        orderBy: {
            title: 'Sort by',
            type: String,
            fulltype: 'select',
            typeinfo: {
                text: 'name',
                value: 'value',
                multiple: false,
                getItems() {
                    return [
                        { name: 'Name', value: 'name' },
                        { name: 'Start date', value: 'start_date' },
                        { name: 'End date', value: 'end_date' },
                        { name: 'Price (Lowest to Highest)', value: 'price_asc' },
                        { name: 'Price (Highest to Lowest)', value: 'price_desc' },
                        { name: 'Registration end date', value: 'registration_end_date' },
                        { name: 'Created', value: 'created_date'}
                    ];
                },
            },
            default: () => 'name'
        },
        width: {
            title: 'Width',
            type: String,
            fulltype: 'csssize',
            default: '200px'
        },
        link: {
            title: 'Page with course content',
            type: String,
            fulltype: 'page'
        },
        maxNumberOfCourses: {
            title: 'Max number of courses displayed',
            typeinfo: {
                placeholder: 'Show all courses'
            },
            type: Number
        },
        showCategoryTitle: {
            title: 'Show category names as headings',
            type: Boolean,
            default: true
        },
        showCategoryDescription: {
            title: 'Show category description under the heading',
            type: Boolean,
            default: true
        },
        showPrice: {
            title: 'Show price',
            type: Boolean,
            default: true
        },
        showSpaceLeft: {
            title: 'Show number of places left',
            type: Boolean,
            default: true
        },
        globalSiteSetting: {
            title: 'Filter by globally selected site',
            type: Boolean,
            default: true
        },
        sites: {
            title: 'Filter by site',
            type: Array,
            fulltype: 'select',
            typeinfo: {
                getItems() {
                    return window.$zoeziapi.get('/api/public/settings/get').then(settings => {
                        return settings.sites.sort((a,b) => a.name.localeCompare(b.name));
                    });
                },
                text: 'name',
                value: 'id',
                multiple: true,
                placeholder: 'Show all sites',
                visible: '!globalSiteSetting'
            },
            default: () => []
        },
        showResourcesFilter: {
            title: 'Show resource filter',
            type: Boolean,
            default: false
        },
        showTagsFilter: {
            title: 'Show tags filter',
            type: Boolean,
            default: false
        },
        resourcesFilter: {
            title: 'Filter by resource',
            type: Array,
            fulltype: 'select',
            typeinfo: {
                getItems() {
                    return window.$zoeziapi.get('/api/member/get/full').then(all => {
                        return all.filter(x => x.type == 'resource' && x.resourceType == 'location' );
                    });
                },
                text: 'lastname',
                value: 'id',
                multiple: true,
                placeholder: 'Show all resources'
            },
            default: () => []
        },
        tagsFilter: {
            title: 'Filter by tags',
            type: Array,
            fulltype: 'select',
            typeinfo: {
                getItems() {
                    return window.$zoeziapi.get('/api/systemtag/get').then(all => {
                        return all;
                    });
                },
                text: 'name',
                value: 'id',
                multiple: true,
                placeholder: 'Show all tags'
            },
            default: () => []
        },
        currentCourseId: {
            title: 'Show course - ID',
            type: Number
        },
        currentCourseName: {
            title: 'Show course - name',
            type: String
        },
    },

    components: {
        CourseCard,
        CourseBooking,
        CardLayout
    },

    data: () => ({
        courses: [],
        courseDialog: {
            course: null,
            show: false
        },
        unsubscribe: null
    }),

    computed: {
        cards() {
            return this.courses.map(course => {
                let title = course.name;
                let image = course.imagekey;
                let shortDescription = course.description;
                let config = {title, image, shortDescription, course};

                return config;
            });
        },
        resources() {
            let resources = [];
            this.coursesOnFilteredSites.forEach(course => {
                course.resources.forEach(r => {
                    if (!resources.find(x => x.id == r.id)) {
                        resources.push(r);
                    }
                });
            })
            return resources.sort((a,b) => a.name.localeCompare(b.name));
        },
        availableTags() {
            let tags = [];
            this.coursesOnFilteredSites.forEach(course => {
                if (course.tags) {
                    course.tags.forEach(tag => {
                        if (!tags.find(x => x.id == tag.id)) {
                            tags.push(tag);
                        }
                    });
                }
            });
            return tags.sort((a, b) => a.name.localeCompare(b.name));
        },
        filterOnSites() {
            if (this.globalSiteSetting && this.$store.state.selectedSiteId) {
                return [this.$store.state.selectedSiteId];
            }

            if (!this.sites || this.sites.length == 0) {
                return null;
            }

            return this.sites;
        },
        coursesOnFilteredSites() {
            return this.courses.filter(course => {
                if (!this.filterOnSites) {
                    return true;
                }

                return intersection(this.filterOnSites, course.sites).length > 0;
            });
        },
        filteredCourses() {
            return this.coursesOnFilteredSites.filter(course => {
                if (this.resourcesFilter && this.resourcesFilter.length) {
                    if (!intersection(course.resources.map(x => x.id), this.resourcesFilter).length) {
                        return false;
                    }
                }
                if (this.tagsFilter && this.tagsFilter.length) {
                    if (!intersection(course.tags.map(x => x.id), this.tagsFilter).length) {
                        return false;
                    }
                }

                return true;
            });
        },
        truncatedCourses() {
            if (this.maxNumberOfCourses) {
                return this.filteredCourses.slice(0, this.maxNumberOfCourses);
            }

            return this.filteredCourses;
        },
        orderedCourses() {
            if (!this.orderBy) {
                return this.truncatedCourses;
            }

            // Fetches the locale from the store, defaulting to 'sv' if not set
            let locale = this.$store.state.settings.language || 'sv';

            return this.truncatedCourses.toSorted((a, b) => {
                switch (this.orderBy) {
                    case 'name':
                        return a.name.localeCompare(b.name, locale, { sensitivity: 'base' });
                    case 'start_date':
                        return new Date(a.startTime) - new Date(b.startTime);
                    case 'end_date':
                        return new Date(a.endTime) - new Date(b.endTime);
                    case 'price_asc':
                        return a.price - b.price;
                    case 'price_desc':
                        return b.price - a.price;
                    case 'registration_end_date':
                        return new Date(a.lastBookingDate) - new Date(b.lastBookingDate);
                    case 'created':
                        return 0; // Do not sort
                    default:
                        console.warn('Unknown orderBy value:', this.orderBy);
                        return 0;
                }
            });
        },
        category() {
            let categories = this.$store.state.settings?.coursecategories;
            return categories?.find(c => c.id == this.categoryId);
        },
        categoryName() {
            return this.category?.name;
        },
        categoryDescription() {
            return this.category?.description;
        }
    },
    methods: {
        toggleTagFilter(tagId) {
            const filter = [...this.tagsFilter];
            const index = filter.indexOf(tagId);
            if (index >= 0) {
                filter.splice(index, 1);
            } else {
                filter.push(tagId);
            }
            this.setProp('tagsFilter', filter);
        },
    },
    watch: {
        categoryId: {
            immediate: true,
            handler() {
                this.courses = [];
                if (this.categoryId) {
                    this.getCourses();
                }
            }
        },
        filterOnSites() {
            this.courses = [];
            if (this.categoryId) {
                this.getCourses();
            }
        },
        'courseDialog.course': {
            immediate: false,
            handler(currentCourse) {
                let id = currentCourse ? currentCourse.id : null;
                if (id != this.currentCourseId) {
                    this.setProp('currentCourseId', id);
                }
                let title = currentCourse ? makeValidPathFromTitle(currentCourse.name).substring(1) : null;
                if (title != this.currentCourseName) {
                    this.setProp('currentCourseName', title);
                }
            }
        },
        'courseDialog.show'(show) {
            if (!show) {
                this.courseDialog.course = null;
            }
        }
    },
    mounted() {
        this.unsubscribe = this.$root.$api.subscribe({type: 'course', callback: () => this.getCourses()})
    },
    beforeCreate() {
        this.getCourses = debounce(() => {
            let sites = this.filterOnSites ? this.filterOnSites.join(',') : undefined;
            this.$api.get('/api/public/course/get', {category: this.categoryId, bookable: true, sites}).then(courses => {
                this.courses = courses;
                this.courseDialog.loading = true;
                if (this.currentCourseId) {
                    this.courseDialog.show = true;
                    this.courseDialog.course = this.courses.find(x => x.id == this.currentCourseId);
                } else if (this.currentCourseName) {
                    this.courseDialog.show = true;
                    let course = this.courses.find(x => makeValidPathFromTitle(x.name).substring(1) == this.currentCourseName);
                    if (course) {
                        this.courseDialog.course = course;
                    }
                }
                if (this.courseDialog.show && this.courseDialog.course == null) {
                    if (this.currentCourseId) {
                        this.courseDialog.loading = false;
                        this.courseDialog.course = { id: this.currentCourseId };
                    } else if (this.currentCourseName) {
                        this.$api.get('/api/public/course/get', {category: this.categoryId, bookable: true}).then(c => {
                            this.courseDialog.course = c.find(x => makeValidPathFromTitle(x.name).substring(1) == this.currentCourseName);
                            this.courseDialog.loading = false;
                        });
                    }
                }
                else {
                    this.courseDialog.loading = false;
                }
            });
        }, 500);
    },
    destroyed() {
        this.unsubscribe?.();
    }
}
</script>
